import {
  CloudWatchAlarmState,
  CloudWatchLogLevel,
} from 'src/api/ProactApiClient';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {ISelectOption} from 'src/types/Form';

export interface CloudWatchStateMeta {
  label: string;
  color: string;
}

export const ALARM_STATE_META: Record<
  CloudWatchAlarmState,
  CloudWatchStateMeta
> = {
  [CloudWatchAlarmState.Alarm]: {label: 'In alarm', color: 'red'},
  [CloudWatchAlarmState.Ok]: {label: 'OK', color: 'green'},
  [CloudWatchAlarmState.InsufficientData]: {
    label: 'Insufficient data',
    color: 'gray',
  },
};

export const LOG_LEVEL_META: Record<CloudWatchLogLevel, CloudWatchStateMeta> = {
  [CloudWatchLogLevel.All]: {label: 'All levels', color: 'gray'},
  [CloudWatchLogLevel.Error]: {label: 'Error', color: 'red'},
  [CloudWatchLogLevel.Warning]: {label: 'Warning', color: 'orange'},
  [CloudWatchLogLevel.Information]: {label: 'Information', color: 'blue'},
  [CloudWatchLogLevel.Debug]: {label: 'Debug', color: 'gray'},
};

// Information is omitted: CLEF leaves `@l` off Information lines, so filtering on it
// matches everything and reads as a broken filter.
export const LOG_LEVEL_OPTIONS: ISelectOption[] = [
  CloudWatchLogLevel.All,
  CloudWatchLogLevel.Error,
  CloudWatchLogLevel.Warning,
  CloudWatchLogLevel.Debug,
].map(level => ({id: String(level), label: LOG_LEVEL_META[level].label}));

/** Metric chart range presets; values are the `hours` query parameter. */
export const METRIC_RANGE_OPTIONS = [
  {value: '1', label: '1h'},
  {value: '3', label: '3h'},
  {value: '24', label: '24h'},
  {value: '168', label: '7d'},
];

/** Log search window presets; values are minutes back from now. */
export const LOG_RANGE_OPTIONS = [
  {value: '15', label: '15m'},
  {value: '60', label: '1h'},
  {value: '180', label: '3h'},
  {value: '1440', label: '24h'},
  {value: '10080', label: '7d'},
];

export const DEFAULT_METRIC_HOURS = 24;
export const DEFAULT_LOG_RANGE_MINUTES = 60;
export const LOG_PAGE_LIMIT = 200;

const COMPARISON_SYMBOLS: Record<string, string> = {
  GreaterThanThreshold: '>',
  GreaterThanOrEqualToThreshold: '≥',
  LessThanThreshold: '<',
  LessThanOrEqualToThreshold: '≤',
};

/** ISO strings, not Dates: the window is a redux query arg and must stay serializable. */
export const logWindowFromMinutes = (
  minutes: number
): {start: string; end: string} => {
  const end = new Date();
  return {
    start: new Date(end.getTime() - minutes * 60_000).toISOString(),
    end: end.toISOString(),
  };
};

export const formatThreshold = (
  comparisonOperator?: string,
  threshold?: number
): string => {
  if (threshold === undefined || threshold === null) return '—';
  if (!comparisonOperator) return String(threshold);
  const symbol = COMPARISON_SYMBOLS[comparisonOperator] ?? comparisonOperator;
  return `${symbol} ${threshold}`;
};

export const formatMetricIdentity = (
  namespace?: string,
  metricName?: string
): string => {
  if (!namespace && !metricName) return '—';
  return [namespace, metricName].filter(Boolean).join('/');
};

export const formatPeriod = (periodSeconds?: number): string => {
  if (!periodSeconds) return '—';
  if (periodSeconds % 3600 === 0) return `${periodSeconds / 3600}h`;
  if (periodSeconds % 60 === 0) return `${periodSeconds / 60}m`;
  return `${periodSeconds}s`;
};

export const formatRetention = (retentionDays?: number): string =>
  retentionDays ? `${retentionDays}d retention` : 'Never expires';

export const formatStoredBytes = (storedBytes?: number): string => {
  if (storedBytes === undefined || storedBytes === null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = storedBytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
};

/**
 * Log lines need second precision to be correlated, which no shared formatter
 * offers; rendered in the viewer's timezone like the CloudWatch console.
 */
export const formatLogTimestamp = (value?: Date | string): string => {
  if (!value) return '—';
  return parseUtcDate(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

/** Chart x-axis label: day + hour for multi-day ranges, clock time otherwise. */
export const formatChartTick = (
  value: Date | string,
  hours: number
): string => {
  const date = parseUtcDate(value);
  if (hours > 24) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      hour12: false,
    });
  }
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};
